import React, { useMemo, useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
  FloatingPortal,
} from '@floating-ui/react';
import { useOptionalI18n } from '@/i18n';
import { localizeOptions, localizeStringOptions, localizeTemplate, localizeText } from '@/i18n/options';

interface SearchableDropdownProps {
  options: string[] | Array<{ value: string | number; label: string }>;
  value: string | number;
  onChange: (value: string | number) => void;
  placeholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  labelClassName?: string;
  showChevron?: boolean;
  menuWidthMode?: 'reference' | 'content';
  menuMinWidth?: number;
  truncate?: boolean;
  allowCustom?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  searchable = false,
  disabled = false,
  className = '',
  buttonClassName = '',
  labelClassName = '',
  showChevron = true,
  menuWidthMode = 'reference',
  menuMinWidth,
  truncate = true,
  allowCustom = false,
}) => {
  const { locale } = useOptionalI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const referenceRef = useRef<HTMLElement | null>(null);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements, availableHeight, availableWidth }) {
          Object.assign(elements.floating.style, {
            width:
              menuWidthMode === 'content'
                ? 'max-content'
                : `${Math.max(rects.reference.width, menuMinWidth ?? 0)}px`,
            minWidth:
              menuWidthMode === 'content'
                ? `${menuMinWidth ?? 0}px`
                : `${Math.max(rects.reference.width, menuMinWidth ?? 0)}px`,
            maxWidth: `${Math.max(availableWidth - 8, 0)}px`,
            '--dropdown-max-height': `${Math.min(availableHeight - 8, 240)}px`,
          } as React.CSSProperties);
        },
      }),
    ],
  });

  const normalizedOptions = useMemo(
    () =>
      (typeof options[0] === 'string'
        ? localizeStringOptions(locale, options as string[])
        : localizeOptions(locale, options as Array<{ value: string | number; label: string }>)),
    [locale, options]
  );

  const filteredOptions = searchable
    ? normalizedOptions.filter((opt) => opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : normalizedOptions;

  const localizedPlaceholder = localizeText(locale, placeholder);
  const displayLabel =
    normalizedOptions.find((opt) => opt.value === value)?.label ||
    (allowCustom && value ? String(value) : localizedPlaceholder);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const referenceNode = referenceRef.current;
      const floatingNode = refs.floating.current;

      if (referenceNode?.contains(target) || floatingNode?.contains(target)) return;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [refs.floating]);

  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCustomSelect = () => {
    if (searchTerm.trim()) {
      onChange(searchTerm.trim());
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const setReferenceNode = (node: HTMLElement | null) => {
    referenceRef.current = node;
    refs.setReference(node);
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <div className="relative">
        {searchable && isOpen ? (
          <input
            ref={(node) => {
              inputRef.current = node;
              setReferenceNode(node);
            }}
            type="text"
            placeholder={localizedPlaceholder}
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            disabled={disabled}
            className="input-field pr-10 focus:ring-2 focus:ring-primary focus:border-primary"
          />
        ) : (
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            ref={setReferenceNode}
            className={`input-field w-full flex items-center justify-between text-left ${
              disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''
            } ${buttonClassName}`}
          >
            <span className={`${value ? 'text-gray-900' : 'text-gray-500'} ${truncate ? 'truncate' : ''} ${labelClassName}`}>
              {isOpen && searchable ? searchTerm : displayLabel}
            </span>
            {showChevron && (
              <div className="ml-2 flex flex-shrink-0 items-center gap-1 pointer-events-none">
                <ChevronDown
                  className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
              </div>
            )}
          </button>
        )}
      </div>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-[1040] rounded-lg border border-primary/20 bg-white shadow-lg"
          >
            <ul className="overflow-y-auto" style={{ maxHeight: 'var(--dropdown-max-height, 240px)' }}>
              {filteredOptions.length > 0 ? (
                <>
                  {filteredOptions.map((option) => (
                    <li key={option.value}>
                      <button
                        type="button"
                        onClick={() => handleSelect(String(option.value))}
                        className={`flex w-full items-center justify-between px-4 py-2 text-left transition-colors ${
                          value === option.value
                            ? 'bg-primary/10 text-primary hover:bg-primary/10'
                            : 'hover:bg-primary/5'
                        }`}
                      >
                        <span>{option.label}</span>
                        {value === option.value && <span className="text-primary">✓</span>}
                      </button>
                    </li>
                  ))}
                  {allowCustom &&
                    searchTerm.trim() &&
                    !filteredOptions.find((opt) => opt.label.toLowerCase() === searchTerm.trim().toLowerCase()) && (
                      <li className="border-t border-gray-100 px-4 py-2">
                        <button
                          type="button"
                          onClick={handleCustomSelect}
                          className="w-full text-left text-sm font-medium text-primary hover:text-primary"
                        >
                          {localizeTemplate(locale, 'Use "{value}" as custom input', searchTerm.trim())}
                        </button>
                      </li>
                    )}
                </>
              ) : (
                <li className="px-4 py-2 text-sm text-gray-500">
                  {allowCustom && searchTerm.trim() ? (
                    <button
                      type="button"
                      onClick={handleCustomSelect}
                      className="w-full text-left text-sm font-medium text-primary"
                    >
                      {localizeTemplate(locale, 'Use "{value}" as custom input', searchTerm.trim())}
                    </button>
                  ) : (
                    localizeText(locale, 'No options found')
                  )}
                </li>
              )}
            </ul>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default SearchableDropdown;
